import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Accordion,
  Badge,
  InputGroup,
} from "react-bootstrap";

const SerialNumberPanel = ({
  show,
  onHide,
  onSubmit,
  serialItems = [],
  rackOptions = [],
  title = "Enter Serial Numbers",
  onOpenRackPicker,
  pendingSerialUpdate = null,
}) => {
  const [serials, setSerials] = useState({});
  const [itemRacks, setItemRacks] = useState({});
  const [errors, setErrors] = useState({});
  const [openAccordion, setOpenAccordion] = useState(null);
  const [activeMode, setActiveMode] = useState("manual");
  const [bulkInputs, setBulkInputs] = useState({});
  const [sequenceInputs, setSequenceInputs] = useState({});
  const [confirmations, setConfirmations] = useState({});
  const [bulkErrors, setBulkErrors] = useState({});
  const [sequenceErrors, setSequenceErrors] = useState({});
  const [bulkAttempted, setBulkAttempted] = useState({});
  const [receiveCounts, setReceiveCounts] = useState({});

  const getItemKey = (item) =>
    String(
      item.order_item_id ||
        item.orderItemId ||
        `new-${item.product_id || item.productId}`,
    );

  const itemNeedsRack = () => rackOptions.length > 0;

  const isSerialItem = (item) => {
    const type = String(item?.inventory_type || item?.inventoryType || item?.inventory_item_type || "").toLowerCase();
    return type.includes("serial");
  };

  const groupedItems = serialItems.reduce((acc, item) => {
    const wh = item.warehouse_name || "Unassigned Warehouse";
    if (!acc[wh]) acc[wh] = [];
    acc[wh].push(item);
    return acc;
  }, {});

  useEffect(() => {
    if (show) {
      const initial = {};
      const initialBulk = {};
      const initialSeq = {};
      const initialConf = {};
      const initialRacks = {};
      const initialCounts = {};
      serialItems.forEach((item) => {
        const key = getItemKey(item);
        const received = item.received_quantity ?? item.receivedQuantity ?? 0;
        const count = item.newQuantity ?? Math.max(0, item.quantity - received) ?? 0;
        initial[key] = Array(count).fill("");
        initialBulk[key] = "";
        initialSeq[key] = "";
        initialConf[key] = false;
        initialRacks[key] = item.rack_id || null;
        initialCounts[key] = count;
      });
      setSerials(initial);
      setBulkInputs(initialBulk);
      setSequenceInputs(initialSeq);
      setConfirmations(initialConf);
      setItemRacks(initialRacks);
      setReceiveCounts(initialCounts);
      setErrors({});
      setBulkErrors({});
      setSequenceErrors({});
      setBulkAttempted({});
      setActiveMode("manual");
      if (serialItems.length > 0) setOpenAccordion(getItemKey(serialItems[0]));
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setItemRacks((prev) => {
      const next = { ...prev };
      let changed = false;
      serialItems.forEach((item) => {
        const key = getItemKey(item);
        if (item.rack_id && next[key] !== item.rack_id) {
          next[key] = item.rack_id;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [serialItems, show]);

  useEffect(() => {
    if (!show) return;
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      serialItems.forEach((item) => {
        const key = getItemKey(item);
        if ((item.rack_id || itemRacks[key]) && next[key]?.rack) {
          delete next[key].rack;
          if (Object.keys(next[key]).length === 0) delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [itemRacks, serialItems, show]);

  const handleReceivedCountChange = (key, value) => {
    const valInt = parseInt(value, 10);
    const item = serialItems.find((i) => getItemKey(i) === key);
    const maxAllowed = item ? item.quantity - (item.received_quantity || 0) : 999999;
    
    let newCount = value === "" ? "" : isNaN(valInt) ? 0 : Math.max(0, valInt);
    let quantityError = newCount > maxAllowed ? `Cannot exceed ${maxAllowed} units` : null;

    setReceiveCounts((prev) => ({ ...prev, [key]: newCount }));
    
    setErrors((prev) => {
      const next = { ...prev };
      if (quantityError) {
        next[key] = { ...(next[key] && !Array.isArray(next[key]) ? next[key] : {}), quantity: quantityError };
      } else if (next[key] && !Array.isArray(next[key])) {
        delete next[key].quantity;
        if (Object.keys(next[key]).length === 0) delete next[key];
      }
      return next;
    });

    const effectiveSerialCount = Math.min(newCount, maxAllowed);

    setSerials((prev) => {
      const currentArr = prev[key] || [];
      if (currentArr.length === effectiveSerialCount) return prev;
      const nextArr = [...currentArr];
      if (effectiveSerialCount > currentArr.length) {
        for (let i = currentArr.length; i < effectiveSerialCount; i++) nextArr.push("");
      } else {
        nextArr.splice(effectiveSerialCount);
      }
      return { ...prev, [key]: nextArr };
    });
  };

  const handleBulkChange = (key, value) => {
    const noSpaces = value.replace(/[ \t]+/g, "");
    setBulkInputs((prev) => ({ ...prev, [key]: noSpaces }));
  };

  const processBulkPaste = (key, count) => {
    const text = bulkInputs[key] || "";
    const lines = text.split(/\n/).map((l) => l.trim()).filter((l) => l !== "");
    setBulkAttempted((prev) => ({ ...prev, [key]: true }));
    const localErrors = [];
    if (lines.length !== count) localErrors.push(`Please provide exactly ${count} serial numbers`);
    if (lines.some((l) => l.length < 5 || l.length > 64)) localErrors.push("Serial number must be between 5 and 64 characters");
    if (new Set(lines).size !== lines.length) localErrors.push("Some serial numbers have already been added");
    if (localErrors.length > 0) {
      setErrors((prev) => ({ ...prev, [key]: localErrors }));
      return;
    }
    setSerials((prev) => ({ ...prev, [key]: lines }));
    setErrors((prev) => ({ ...prev, [key]: null }));
    setActiveMode("manual");
  };

  const applySequence = (key, count) => {
    const startVal = sequenceInputs[key] || "";
    if (!confirmations[key]) {
      setSequenceErrors((prev) => ({ ...prev, [key]: "Please confirm that these arrived in sequential order" }));
      return;
    }
    const match = startVal.match(/^(.*?)(\d+)$/);
    if (!match) {
      setSequenceErrors((prev) => ({ ...prev, [key]: "Serial number must end with a number to use the sequence generator" }));
      return;
    }
    const prefix = match[1];
    const numStr = match[2];
    const startNum = parseInt(numStr, 10);
    const length = numStr.length;
    const generated = [];
    for (let i = 0; i < count; i++) {
      const currentNum = (startNum + i).toString();
      const paddedNum = currentNum.padStart(length, "0");
      const fullSN = prefix + paddedNum;
      if (fullSN.length < 5 || fullSN.length > 64) {
        setSequenceErrors((prev) => ({ ...prev, [key]: "Serial number must be between 5 and 64 characters" }));
        return;
      }
      generated.push(fullSN);
    }
    setSerials((prev) => ({ ...prev, [key]: generated }));
    setSequenceErrors((prev) => ({ ...prev, [key]: null }));
    setErrors((prev) => ({ ...prev, [key]: null }));
    setActiveMode("manual");
  };

  const handleSerialChange = (key, index, value) => {
    const noSpaces = value.replace(/\s+/g, "");
    
    setSerials(prev => {
      const nextArr = [...(prev[key] || [])];
      nextArr[index] = noSpaces;
      
      setErrors(prevErrors => {
        const nextErrors = { ...prevErrors };
        
        const allValues = [];
        serialItems.forEach(item => {
          const k = getItemKey(item);
          const arr = k === key ? nextArr : (serials[k] || []);
          arr.forEach((val, i) => {
            if (val && val.trim()) allValues.push({ k, i, val: val.trim() });
          });
        });
        const counts = {};
        allValues.forEach(v => { counts[v.val] = (counts[v.val] || 0) + 1; });

        const getSerialErrors = (e) => {
          if (!e) return [];
          if (Array.isArray(e)) return [...e];
          if (e._serialErrors) return [...e._serialErrors];
          return [];
        };

        const getPreserved = (e) => {
          if (!e || Array.isArray(e)) return {};
          const p = {};
          if (e.rack) p.rack = e.rack;
          if (e.quantity) p.quantity = e.quantity;
          return p;
        };

        const applyErrors = (k, serialErrs, preserved) => {
          const hasSerial = serialErrs.some(e => e);
          if (hasSerial) {
            nextErrors[k] = Object.keys(preserved).length > 0
              ? { _serialErrors: serialErrs, ...preserved }
              : serialErrs;
          } else if (Object.keys(preserved).length > 0) {
            nextErrors[k] = preserved;
          } else {
            delete nextErrors[k];
          }
        };

        const curSerialErrs = getSerialErrors(nextErrors[key]);
        const curPreserved = getPreserved(nextErrors[key]);
        const trimmed = noSpaces.trim();
        if (!trimmed) {
          curSerialErrs[index] = null; 
        } else if (trimmed.length < 5 || trimmed.length > 64) {
          curSerialErrs[index] = "Serial number must be between 5 and 64 characters";
        } else if (counts[trimmed] > 1) {
          curSerialErrs[index] = "This serial number has already been added";
        } else {
          curSerialErrs[index] = null; 
        }
        applyErrors(key, curSerialErrs, curPreserved);

        serialItems.forEach(item => {
          const k = getItemKey(item);
          const arr = k === key ? nextArr : (serials[k] || []);
          const otherSerialErrs = getSerialErrors(nextErrors[k]);
          const otherPreserved = getPreserved(nextErrors[k]);
          let changed = false;

          arr.forEach((v, i) => {
            if (k === key && i === index) return; 
            const t = (v || "").trim();
            if (!t) return;
            const curErr = otherSerialErrs[i];
            const isDupErr = curErr && curErr.includes("already been added");
            const isDupNow = counts[t] > 1;
            if (isDupErr && !isDupNow) {
              otherSerialErrs[i] = null; 
              changed = true;
            } else if (!curErr && isDupNow) {
              otherSerialErrs[i] = "This serial number has already been added"; 
              changed = true;
            }
          });

          if (changed) applyErrors(k, otherSerialErrs, otherPreserved);
        });

        return nextErrors;
      });

      return { ...prev, [key]: nextArr };
    });
  };

  const handleSerialBlur = (key, index, value) => {
    const val = (value || "").trim();
    if (val === "") return;
    setErrors((prev) => {
      const next = { ...prev };
      if (!next[key] || !Array.isArray(next[key])) next[key] = [];
      else next[key] = [...next[key]];

      if (val.length < 5 || val.length > 64) {
        next[key][index] = "Serial number must be between 5 and 64 characters";
      } else if (next[key][index] && next[key][index].includes("characters")) {
        next[key][index] = null;
      }
      return next;
    });
  };

  const handleModeChange = (mode) => {
    if (mode === activeMode) return;
    const newBulkInputs = { ...bulkInputs };
    const newSerials = { ...serials };

    serialItems.forEach((item) => {
      const key = getItemKey(item);
      if (!isSerialItem(item)) return;
      if (mode === "bulk") {
        const currentManual = serials[key] || [];
        newBulkInputs[key] = currentManual.filter((s) => s).join("\n");
      } else if (mode === "manual") {
        const lines = (bulkInputs[key] || "").split("\n").map((l) => l.trim()).filter((l) => l !== "");
        const count = receiveCounts[key] || 0;
        const manual = Array(count).fill("");
        lines.forEach((l, i) => { if (i < count) manual[i] = l; });
        newSerials[key] = manual;
      }
    });

    setBulkInputs(newBulkInputs);
    setSerials(newSerials);
    setBulkAttempted({});
    setErrors({});
    setActiveMode(mode);
  };

  const validate = () => {
    let valid = true;
    const newErrors = {};

    serialItems.forEach((item) => {
      const key = getItemKey(item);
      const count = receiveCounts[key] || 0;
      const remaining = item.quantity - (item.received_quantity || 0);
      const serial = isSerialItem(item);

      if (count <= 0) {
        valid = false;
        if (!newErrors[key] || Array.isArray(newErrors[key])) newErrors[key] = {};
        newErrors[key].quantity = "Quantity must be greater than 0";
      } else if (count > remaining) {
        valid = false;
        if (!newErrors[key] || Array.isArray(newErrors[key])) newErrors[key] = {};
        newErrors[key].quantity = `Cannot exceed ${remaining} units`;
      }

      if (itemNeedsRack() && !itemRacks[key]) {
        valid = false;
        if (!newErrors[key] || Array.isArray(newErrors[key]))
          newErrors[key] = {};
        newErrors[key].rack = "Location is required";
      }

      if (!serial) return;

      if (activeMode === "bulk") {
        const text = bulkInputs[key] || "";
        const lines = text.split(/\n/).map((l) => l.trim()).filter((l) => l !== "");
        const localErrors = [];
        if (lines.length !== count) localErrors.push(`Please provide exactly ${count} serial numbers`);
        if (lines.some((l) => l.length < 5 || l.length > 64)) localErrors.push("Serial number must be between 5 and 64 characters");
        if (new Set(lines).size !== lines.length) localErrors.push("Some serial numbers have already been added");
        if (localErrors.length > 0) {
          valid = false;
          newErrors[key] = localErrors;
          return;
        }
      } else {
        const arr = serials[key] || [];
        const itemErrors = [];
        for (let i = 0; i < count; i++) {
          const val = (arr[i] || "").trim();
          if (val === "") { itemErrors[i] = "Serial number is required"; valid = false; }
          else if (val.length < 5 || val.length > 64) { itemErrors[i] = "Serial number must be between 5 and 64 characters"; valid = false; }
        }
        if (itemErrors.some((e) => e)) {
          if (newErrors[key] && !Array.isArray(newErrors[key])) newErrors[key]._serialErrors = itemErrors;
          else newErrors[key] = itemErrors;
        }
      }
    });

    setErrors(newErrors);
    if (!valid) return { valid: false, errors: newErrors };

    const allSerialsFlat = [];
    serialItems.forEach((item) => {
      const k = getItemKey(item);
      const count = item.newQuantity ?? item.quantity ?? 0;
      let values = [];
      if (activeMode === "bulk") {
        values = (bulkInputs[k] || "").split("\n").map((l) => l.trim()).filter((l) => l !== "");
      } else {
        values = (serials[k] || []).map((s) => (s || "").trim());
      }
      values.forEach((val, i) => { if (val) allSerialsFlat.push({ key: k, index: i, value: val }); });
    });

    const seen = {};
    allSerialsFlat.forEach(({ value }) => { seen[value] = (seen[value] || 0) + 1; });
    const duplicates = new Set(Object.keys(seen).filter((v) => seen[v] > 1));

    if (duplicates.size > 0) {
      const dupErrors = { ...newErrors };
      allSerialsFlat.forEach(({ key, index, value }) => {
        if (duplicates.has(value)) {
          if (!dupErrors[key]) dupErrors[key] = [];
          if (Array.isArray(dupErrors[key])) dupErrors[key][index] = "This serial number has already been added";
          valid = false;
        }
      });
      setErrors(dupErrors);
      return { valid: false, errors: dupErrors };
    }

    return { valid: true, errors: newErrors };
  };

  const handleSubmit = () => {
    if (activeMode === "bulk") {
      const newSerials = { ...serials };
      let hasBulkError = false;
      const newBulkAttempted = { ...bulkAttempted };

      serialItems.forEach((item) => {
        if (!isSerialItem(item)) return;
        const key = getItemKey(item);
        newBulkAttempted[key] = true;
        const count = receiveCounts[key] || 0;
        const lines = (bulkInputs[key] || "")
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l !== "");
        if (lines.length === count && !lines.some((l) => l.length < 5 || l.length > 64)) {
          newSerials[key] = lines;
        } else {
          hasBulkError = true;
        }
      });

      setBulkAttempted(newBulkAttempted);

      if (hasBulkError) {
        const r = validate();
        const firstErrorItem = serialItems.find((item) => {
          const key = getItemKey(item);
          return r.errors[key] || !itemRacks[key];
        });
        if (firstErrorItem) {
          const k = getItemKey(firstErrorItem);
          setOpenAccordion(k);
          setTimeout(() => {
            const el = document.getElementById(`accordion-item-${k}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 300);
        }
        return;
      }
      setSerials(newSerials);
    }

    const result = validate();
    if (!result.valid) {
      const firstErrorItem = serialItems.find((item) => {
        const key = getItemKey(item);
        return result.errors[key] || !itemRacks[key];
      });
      if (firstErrorItem) {
        const k = getItemKey(firstErrorItem);
        setOpenAccordion(k);
        setTimeout(() => {
          const el = document.getElementById(`accordion-item-${k}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
      }
      return;
    }

    const trimmedSerials = {};
    Object.keys(serials).forEach((key) => {
      trimmedSerials[key] = (serials[key] || []).map((s) => (s || "").trim());
    });

    const finalRacks = {};
    serialItems.forEach((item) => {
      const key = getItemKey(item);
      finalRacks[key] = item.rack_id || itemRacks[key] || null;
    });

    onSubmit(trimmedSerials, finalRacks, receiveCounts);
  };

  const getFilledCount = (key) =>
    (serials[key] || []).filter((s) => s && s.trim() !== "").length;

  const keyHasError = (key) => {
    const e = errors[key];
    if (!e) return false;
    if (Array.isArray(e)) return e.some((x) => x);
    if (typeof e === "object") return Object.keys(e).length > 0;
    return false;
  };

  const getSerialErrors = (key) => {
    const e = errors[key];
    if (!e) return [];
    if (Array.isArray(e)) return e;
    if (e._serialErrors) return e._serialErrors;
    return [];
  };

  const getRackError = (key) => {
    const e = errors[key];
    if (!e || Array.isArray(e)) return null;
    return typeof e === "object" ? e.rack || null : null;
  };

  const hasAnySerial = serialItems.some(isSerialItem);
  const isFullReceive = pendingSerialUpdate?.actionType === "full" || pendingSerialUpdate?.type === "receiveAll";
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1440);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1440);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!show) return null;

   return (
    <div className="serial-number-panel h-100 bg-white shadow-lg d-flex flex-column border-start animate__animated animate__slideInRight animate__faster" style={{ zIndex: 1060 }}>
      <style>{`
        .serial-number-panel .extra-small { font-size: ${isSmallScreen ? "0.6rem" : "0.7rem"} !important; }
        .serial-number-panel .h4 { font-size: ${isSmallScreen ? "1.1rem" : "1.5rem"} !important; }
        .serial-number-panel .h5 { font-size: ${isSmallScreen ? "1rem" : "1.25rem"} !important; }
        .serial-number-panel .h6 { font-size: ${isSmallScreen ? "0.85rem" : "1rem"} !important; }
        .serial-number-panel .small { font-size: ${isSmallScreen ? "0.7rem" : "0.875rem"} !important; }
        .serial-number-panel .input-group-text { font-size: ${isSmallScreen ? "0.75rem" : "0.85rem"} !important; padding: ${isSmallScreen ? "0.2rem 0.4rem" : "0.375rem 0.75rem"} !important; min-width: auto !important; }
        .serial-number-panel .form-control-sm { font-size: ${isSmallScreen ? "0.8rem" : "0.875rem"} !important; }
        .serial-number-panel .accordion-button { padding: ${isSmallScreen ? "0.75rem 1rem" : "1rem 1.25rem"} !important; }
        .serial-number-panel .badge { font-size: ${isSmallScreen ? "0.6rem" : "0.7rem"} !important; }
        .serial-number-panel .nav-link { font-size: ${isSmallScreen ? "0.8rem" : "0.9rem"} !important; padding: ${isSmallScreen ? "0.4rem 0.8rem" : "0.5rem 1rem"} !important; }
        @media (max-width: 576px) {
          .serial-number-panel .flex-wrap > div { min-width: 100% !important; }
        }
      `}</style>
      <div className="custom-modal-header d-flex align-items-center position-relative p-3 border-bottom bg-white">
        <div className="d-flex flex-column ps-2">
          <h5 className="modal-title fw-bold mb-0">
            {hasAnySerial ? "Enter Serial Numbers" : "Review Order"}
          </h5>
          <small className="text-muted">
            {hasAnySerial 
              ? "Please enter unique serial numbers for all units that are received" 
              : "Please review the quantities and storage locations for these items"}
          </small>
        </div>
      </div>

      <div
        className="flex-grow-1 overflow-auto p-3"
        style={{
          backgroundColor: "#f8f9fa",
        }}
      >
        {serialItems.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
            <p className="text-muted">No items found.</p>
          </div>
        ) : (
          <div className="px-1">
            {hasAnySerial && (
              <div className="d-flex justify-content-center mb-4">
                <div
                  className="nav-tab-wrapper p-1 bg-white border rounded shadow-sm d-flex w-100"
                  style={{ maxWidth: "600px" }}
                >
                  {[
                    { key: "manual", label: "Manual Scan" },
                    { key: "bulk", label: "Bulk Paste" },
                  ].map(({ key, label }) => {
                    if (key === "bulk" && !hasAnySerial) return null;
                    return (
                      <button
                        key={key}
                        className={`btn flex-grow-1 border-0 py-2 fw-bold ${
                          activeMode === key
                            ? "btn-primary"
                            : "text-primary bg-transparent"
                        }`}
                        onClick={() => handleModeChange(key)}
                        style={{
                          borderRadius: "6px",
                          transition: "all 0.2s",
                          fontSize: "0.85rem",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Accordion
              activeKey={openAccordion}
              onSelect={(k) => setOpenAccordion(k)}
              className="serial-panel-accordion"
            >
              {Object.entries(groupedItems).map(([warehouse, items]) => (
                <div key={warehouse} className="mb-4">
                  {items.map((item) => {
                    const key = getItemKey(item);
                    const maxRemaining = item.newQuantity ?? item.quantity ?? 0;
                    const count = (() => {
                      if (receiveCounts[key] === "") return 0;
                      const rawVal = receiveCounts[key] !== undefined 
                         ? parseInt(receiveCounts[key], 10) 
                         : maxRemaining;
                      const rem = Math.max(0, item.quantity - (item.received_quantity || 0));
                      if (isNaN(rawVal)) return rem;
                      return Math.min(rawVal, rem);
                    })();
                    const filled = getFilledCount(key);
                    const hasError = keyHasError(key);
                    const rackError = getRackError(key);
                    const serialErrors = getSerialErrors(key);
                    const needsRack = itemNeedsRack();
                    const serial = isSerialItem(item);
                    const currentRackLabel =
                      (item.rack_name ? `${item.rack_name}${item.rack_number ? " - " + item.rack_number : ""} (R${item.row_number}C${item.column_number})` : 
                      rackOptions.find((r) => r.value === (itemRacks[key] || item.rack_id))?.label) || "No Location Assigned";

                    return (
                      <Accordion.Item 
                        key={key} 
                        eventKey={key} 
                        className="border rounded shadow-sm mb-3 overflow-hidden bg-white"
                        id={`accordion-item-${key}`}
                      >
                        <Accordion.Header className="serial-accordion-header">
                          <div className="d-flex align-items-center justify-content-between w-100 me-3">
                            <div className="d-flex flex-column gap-1 text-start">
                              <span className="fw-bold text-dark">
                                {item.productName}
                              </span>
                              <div className="d-flex flex-wrap gap-2">
                                {item.warehouse_name && (
                                  <Badge bg="light" text="muted" className="border py-1 fw-normal" style={{ fontSize: "0.65rem" }}>
                                    Warehouse: {item.warehouse_name}
                                  </Badge>
                                )}
                                {item.job_name && (
                                  <Badge bg="light" text="muted" className="border py-1 fw-normal" style={{ fontSize: "0.65rem" }}>
                                    Job: {item.job_name}
                                  </Badge>
                                )}
                                {item.system_name && (
                                  <Badge bg="light" text="muted" className="border py-1 fw-normal" style={{ fontSize: "0.65rem" }}>
                                    Sys: {item.system_name}
                                  </Badge>
                                )}
                                {item.phase_name && (
                                  <Badge bg="light" text="muted" className="border py-1 fw-normal" style={{ fontSize: "0.65rem" }}>
                                    Phase: {item.phase_name}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            <div className="d-flex flex-column align-items-end gap-1 ms-2">
                              {serial && (
                                <Badge
                                  bg={filled === count ? "success" : hasError ? "danger" : "primary"}
                                  className="rounded-pill px-3 py-2"
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {filled}/{count} <span className="d-none d-xl-inline">Units</span>
                                </Badge>
                              )}                     
                            </div>
                          </div>
                        </Accordion.Header>
                         <Accordion.Body className="bg-white border-top p-3 text-start">
                          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4 mb-4 p-3 border rounded bg-light bg-opacity-10" >
                            <div className="d-flex flex-column align-items-start flex-grow-1" style={{ minWidth: "120px" }}>
                              <span className="text-muted extra-small fw-bold text-uppercase letter-spacing-1 mb-1">Ordered Qty</span>
                              <span className="h5 mb-0 fw-bold text-dark">{item.quantity}</span>
                            </div>
                            
                            <div className="d-flex flex-column align-items-start flex-grow-1" style={{ minWidth: "200px" }}>
                              <span className="text-muted extra-small fw-bold text-uppercase letter-spacing-1 mb-1">Received Qty</span>
                              <InputGroup size="sm" className="shadow-sm rounded" style={{ border: errors[key]?.quantity ? "1px solid #dc3545" : "1px solid #ced4da", overflow: "hidden", maxWidth: "220px" }}>
                                <Form.Control
                                  type="number"
                                  className={`border-0 fw-bold ${errors[key]?.quantity ? "text-danger" : ""} ${isFullReceive ? "text-muted" : ""}`}
                                  value={receiveCounts[key] !== undefined ? receiveCounts[key] : count}
                                  placeholder="0"
                                  isInvalid={!!errors[key]?.quantity}
                                  disabled={isFullReceive}
                                  onChange={(e) => handleReceivedCountChange(key, e.target.value)}
                                  style={{  fontSize: "0.95rem", textAlign: "left", boxShadow: "none", backgroundColor: "white" }}
                                />
                                <InputGroup.Text 
                                  className={`bg-white border-0 ps-0 pe-2 fw-medium ${errors[key]?.quantity ? "text-danger" : "text-muted"}`} 
                                  style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}
                                >
                                  = {count + parseInt(item.received_quantity || 0)} / {item.quantity}
                                </InputGroup.Text>
                              </InputGroup>
                              {errors[key]?.quantity && (
                                <div className="text-danger extra-small fw-bold mt-1 animate__animated animate__headShake">
                                  {errors[key].quantity}
                                </div>
                              )}
                            </div>

                            <div className="d-flex flex-column align-items-start flex-grow-1" style={{ minWidth: "200px" }}>
                              <span className="text-muted extra-small fw-bold text-uppercase letter-spacing-1 mb-1">
                                Location<span className="text-danger">*</span>
                              </span>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`small mb-0 fw-bold text-primary`} >
                                  {(() => {
                                    const currentRackId = itemRacks[key] || item.rack_id;
                                    const option = rackOptions.find((r) => r.value === currentRackId);
                                    return option ? option.label : "No Location Assigned";
                                  })()}
                                </span>
                                {onOpenRackPicker && (
                                  <span
                                    className="p-1 d-flex align-items-center justify-content-center text-primary border rounded-circle hover-bg-light"
                                    style={{ cursor: "pointer", width: "32px", height: "32px", transition: "all 0.2s" }}
                                    onClick={() => onOpenRackPicker(item)}
                                  >
                                    <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                      <path fillRule="evenodd" clipRule="evenodd" d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M15.1655 4.60254L19.7315 9.16854" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                )}
                              </div>
                              {rackError && <div className="text-danger extra-small mt-1 fw-bold">{rackError}</div>}
                            </div>
                          </div>

                         

                          {serial && (
                            <div className="serial-inputs-wrapper">
                              <div className="mt-4 mb-2">
                                <span className="fw-bold small text-muted text-uppercase extra-small letter-spacing-1">
                                  Serial Numbers
                                </span>
                              </div>
                              {activeMode === "manual" && (
                                <div className="row g-2 mt-2">
                                  {Array.from({ length: count }, (_, i) => (
                                    <div key={i} className="col-12 col-lg-6">
                                      <InputGroup className="mb-2" hasValidation>
                                        <InputGroup.Text
                                          style={{
                                            backgroundColor: "#f0f4f8",
                                            color: "#4e7397",
                                            fontWeight: "600",
                                            fontSize: "0.85rem",
                                            border: "1px solid #ced4da",
                                            borderRight: "1px solid #e9ecef",
                                            minWidth: "94px",
                                            display: "flex",
                                            justifyContent: "center",
                                            borderRadius: "8px 0 0 8px",
                                          }}
                                        >
                                          Unit # {i + 1}
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          placeholder="Serial Number"
                                          value={(serials[key] || [])[i] || ""}
                                          onChange={(e) => handleSerialChange(key, i, e.target.value)}
                                          onBlur={(e) => handleSerialBlur(key, i, e.target.value)}
                                          isInvalid={!!(serialErrors && serialErrors[i])}
                                          style={{ fontSize: "0.9rem", padding: "0.6rem 1rem" }}
                                        />
                                        {serialErrors && serialErrors[i] && (
                                          <Form.Control.Feedback type="invalid" style={{ fontSize: "0.7rem" }}>
                                            {serialErrors[i]}
                                          </Form.Control.Feedback>
                                        )}
                                      </InputGroup>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {activeMode === "sequence" && (
                                <div className="sequence-gen-container p-3 border rounded bg-light mt-2">
                                  <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold text-dark">Starting Serial Number</Form.Label>
                                    <InputGroup>
                                      <Form.Control
                                        type="text"
                                        placeholder="e.g. SN-1000000000"
                                        maxLength={64}
                                        value={sequenceInputs[key] || ""}
                                        onChange={(e) => setSequenceInputs((prev) => ({ ...prev, [key]: e.target.value.replace(/\s+/g, "") }))}
                                        isInvalid={!!sequenceErrors[key]}
                                        style={{ borderRadius: "8px 0 0 8px", padding: "0.6rem 1rem" }}
                                      />
                                      <Button variant="outline-primary" onClick={() => applySequence(key, count)} style={{ borderRadius: "0 8px 8px 0", padding: "0 1.5rem" }}>Apply</Button>
                                    </InputGroup>
                                    {sequenceErrors[key] && <div className="text-danger small fw-bold mt-1">{sequenceErrors[key]}</div>}
                                  </Form.Group>
                                  <Form.Check
                                    type="checkbox"
                                    id={`confirm-seq-${key}`}
                                    label="Confirm these arrived in sequential order"
                                    checked={confirmations[key] || false}
                                    onChange={(e) => setConfirmations((prev) => ({ ...prev, [key]: e.target.checked }))}
                                    className="small text-muted mb-2"
                                  />
                                  <div className="p-2 border rounded bg-white mt-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <i className="fas fa-magic text-primary small"></i>
                                      <span className="small fw-bold text-muted">Preview</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1" style={{ maxHeight: "80px", overflowY: "auto" }}>
                                      {(() => {
                                        const sVal = sequenceInputs[key] || "";
                                        const m = sVal.match(/^(.*?)(\d+)$/);
                                        if (!m) return <span className="text-muted extra-small italic">Enter starting serial...</span>;
                                        const p = m[1];
                                        const ns = m[2];
                                        const sn = parseInt(ns, 10);
                                        const l = ns.length;
                                        return Array.from({ length: Math.min(count, 5) }, (_, i) => {
                                          const val = p + (sn + i).toString().padStart(l, "0");
                                          return <Badge key={i} bg="primary" className="fw-normal">{val}</Badge>;
                                        });
                                      })()}
                                      {count > 5 && <span className="text-muted extra-small">...and {count - 5} more</span>}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {activeMode === "bulk" && (
                                <div className="bulk-paste-container mt-2">
                                  <div className="position-relative mb-2" style={{ minHeight: "120px" }}>
                                    <div
                                      style={{
                                        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                        padding: "12px", fontSize: "1rem", fontFamily: "monospace",
                                        whiteSpace: "pre-wrap", wordBreak: "break-all", pointerEvents: "none",
                                        color: "transparent", zIndex: 1, borderRadius: "8px", border: "1px solid transparent", overflow: "hidden",
                                      }}
                                    >
                                      {(bulkInputs[key] || "").split("\n").map((line, i) => {
                                        const trimmed = line.trim();
                                        const allLines = (bulkInputs[key] || "").split("\n").map((l) => l.trim()).filter((l) => l !== "");
                                        const isBadLength = trimmed.length > 0 && (trimmed.length < 5 || trimmed.length > 64);
                                        const isDuplicate = trimmed.length > 0 && allLines.filter((l) => l === trimmed).length > 1;
                                        const showHighlight = bulkAttempted[key] && (isBadLength || isDuplicate);
                                        return (
                                          <span key={i} style={{ backgroundColor: showHighlight ? "rgba(255, 0, 0, 0.2)" : "transparent", display: "block", width: "100%" }}>
                                            {line}{"\n"}
                                          </span>
                                        );
                                      })}
                                    </div>
                                    <Form.Control
                                      as="textarea"
                                      rows={count}
                                      placeholder={`Paste ${count} serials here... (One per line)`}
                                      value={bulkInputs[key] || ""}
                                      onChange={(e) => {
                                        const linesArr = e.target.value.split("\n");
                                        if (linesArr.length > count) return;
                                        handleBulkChange(key, e.target.value);
                                      }}
                                      className="form-control-lg"
                                      style={{
                                        fontSize: "1rem", borderRadius: "8px", fontFamily: "monospace", position: "relative", zIndex: 2,
                                        background: "transparent",
                                        border: (() => {
                                          if (!bulkAttempted[key]) return "1px solid #ced4da";
                                          const currentLines = (bulkInputs[key] || "").split("\n").map((l) => l.trim()).filter((l) => l !== "");
                                          const hasDuplicates = new Set(currentLines).size !== currentLines.length;
                                          const hasErr = currentLines.length !== count || currentLines.some((l) => l.length < 5 || l.length > 64) || hasDuplicates;
                                          return hasErr ? "2px solid #dc3545" : "1px solid #28a745";
                                        })(),
                                        lineHeight: "1.5", resize: "none", overflowY: "hidden",
                                      }}
                                    />
                                    {bulkAttempted[key] && (() => {
                                      const currentLines = (bulkInputs[key] || "").split("\n").map((l) => l.trim()).filter((l) => l !== "");
                                      const localErrors = [];
                                      if (currentLines.length !== count) localErrors.push(`Please provide exactly ${count} serial numbers`);
                                      if (currentLines.some((l) => l.length < 5 || l.length > 64)) localErrors.push("Serial number must be between 5 and 64 characters");
                                      if (new Set(currentLines).size !== currentLines.length) localErrors.push("Some serial numbers have already been added");
                                      if (localErrors.length > 0) {
                                        return (
                                          <div className="mt-1 d-flex flex-column gap-1">
                                            {localErrors.map((err, idx) => (
                                              <div key={idx} className="text-danger small fw-bold">{err}</div>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="text-success mt-1 small fw-bold">All serials are valid. Click Process Paste to continue</div>
                                      );
                                    })()}
                                  </div>
                                  <div className="d-flex justify-content-center align-items-center p-2 rounded">
                                    <Button 
                                      variant="primary" 
                                      size="sm" 
                                      onClick={() => processBulkPaste(key, count)}
                                      style={{ borderRadius: "6px" }}
                                    >
                                      Process Paste
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </div>
              ))}
            </Accordion>
          </div>
        )}
      </div>

      <div className="modal-footer p-3 border-top bg-white d-flex justify-content-end gap-2 shadow-sm">
        <Button
          variant="btn btn-primary px-4 py-2 fw-bold"
          onClick={handleSubmit}
        >
          Save
        </Button>
        <Button variant="btn btn-danger" onClick={onHide}>
          Cancel
        </Button>
      </div>

      <style>{`
        .letter-spacing-1 { letter-spacing: 1px; }
        .serial-accordion-header .accordion-button { background-color: white !important; padding: 1rem; }
        .serial-accordion-header .accordion-button:not(.collapsed) { color: #0d6efd; box-shadow: none; border-bottom: 1px solid #eee; }
        .nav-tab-wrapper button { transition: all 0.2s ease; border-radius: 6px; }
        .extra-small { font-size: 0.7rem; }
        .hover-opacity:hover { opacity: 0.8; }
        .serial-number-panel {
          animation-duration: 0.3s;
        }
      `}</style>
    </div>
  );
};

export default SerialNumberPanel;
